import { useEffect, useCallback, useRef, useState, useMemo } from "react";
import type { Dialog } from "@/types/dialog";
import { getLanguageConfig } from "@/lib/language-config";
import { useAuthStore } from "@/features/auth";
import { useCreditsStore } from "@/features/credits";
import { useDialogStore } from "./dialog-store";
import { useTrainingStore } from "@/features/training";

interface RolePlayModalProps {
  dialog: Dialog;
  unlocked: boolean;
  onUnlock: () => void;
}

const LEVEL_GRADIENTS: Record<string, string> = {
  A1: "from-emerald-800 to-teal-900",
  A2: "from-green-800 to-emerald-900",
  B1: "from-amber-800 to-orange-900",
  B2: "from-orange-800 to-red-900",
  C1: "from-purple-800 to-violet-900",
  C2: "from-indigo-800 to-blue-900",
};

const BCP47_MAP: Record<string, string> = {
  CMN_CN: "zh-CN", CMN_TW: "zh-TW", YUE: "zh-HK",
  DEU: "de-DE", FRA: "fr-FR", SPA: "es-ES", ITA: "it-IT",
  POR: "pt-BR", RUS: "ru-RU", JPN: "ja-JP", KOR: "ko-KR",
  CES: "cs-CZ", POL: "pl-PL", TUR: "tr-TR", NLD: "nl-NL",
  SWE: "sv-SE", ARB: "ar-SA", HIN: "hi-IN", THA: "th-TH",
  VIE: "vi-VN", IND: "id-ID", AFR: "af-ZA", BEN: "bn-IN",
  CAT: "ca-ES", DAN: "da-DK", ELL: "el-GR", FIN: "fi-FI",
  HEB: "he-IL", HUN: "hu-HU", NOR: "nb-NO", RON: "ro-RO",
  SLK: "sk-SK", SLV: "sl-SI", SRP: "sr-RS", UKR: "uk-UA",
  ARZ: "ar-EG", AZJ: "az-AZ", BEL: "be-BY", BUL: "bg-BG",
  HRV: "hr-HR", EST: "et-EE", KAT: "ka-GE", LAV: "lv-LV",
  LIT: "lt-LT", MKD: "mk-MK", MSA: "ms-MY", SQI: "sq-AL",
};

function escapeHtml(text: string): string {
  const el = document.createElement("div");
  el.textContent = text;
  return el.innerHTML;
}

function hideWords(text: string, percentage: number): string {
  if (percentage >= 100) return escapeHtml(text);
  if (percentage <= 0) return '<span class="opacity-40">_ _ _</span>';

  const words = text.split(/(\s+)/);
  const wordsOnly = words.filter((w) => w.trim() !== "");
  const totalWords = wordsOnly.length;
  const hideCount = Math.floor(totalWords * (1 - percentage / 100));
  if (hideCount === 0) return escapeHtml(text);

  const indices = new Set<number>();
  while (indices.size < hideCount && indices.size < totalWords) {
    indices.add(Math.floor(Math.random() * totalWords));
  }

  let wordIdx = 0;
  return words
    .map((w) => {
      if (w.trim() === "") return w;
      const result = indices.has(wordIdx)
        ? '<span class="opacity-30">___</span>'
        : escapeHtml(w);
      wordIdx++;
      return result;
    })
    .join("");
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function getSpeechRecognitionClass(): (new () => any) | null {
  const w = window as any;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as (new () => any) | null;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

function MicIcon() {
  return (
    <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

export function RolePlayModal({ dialog, unlocked, onUnlock }: RolePlayModalProps) {
  const rolePlay = useDialogStore((s) => s.rolePlay);
  const setRolePlay = useDialogStore((s) => s.setRolePlay);
  const toggles = useDialogStore((s) => s.toggles);
  const audio = useDialogStore((s) => s.audio);
  const config = getLanguageConfig(dialog.language_code);
  const user = useAuthStore((s) => s.user);
  const emailUser = useAuthStore((s) => s.emailUser);
  const openAuthModal = useAuthStore((s) => s.openModal);
  const openEmailModal = useAuthStore((s) => s.openEmailModal);
  const deductCredit = useCreditsStore((s) => s.deductCredit);
  const deducting = useCreditsStore((s) => s.deducting);
  const markMastered = useTrainingStore((s) => s.markMastered);
  const hiddenWordsCache = useRef<Record<string, string>>({});
  const [creditError, setCreditError] = useState<string | null>(null);
  const [insufficientCredits, setInsufficientCredits] = useState(false);

  const [transcript, setTranscript] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [partnerAudioPlaying, setPartnerAudioPlaying] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);

  // Fix: when all lines have the same speakerIndex but dialog has 2+ speakers, infer alternation
  const speakableLines = useMemo(() => {
    const lines = dialog.lines.filter((l) => l.text);
    if (lines.length < 2 || dialog.speakers.length < 2) return lines;
    const allSame = lines.every((l) => l.speakerIndex === lines[0]!.speakerIndex);
    if (!allSame) return lines;
    return lines.map((line, i) => ({ ...line, speakerIndex: i % dialog.speakers.length }));
  }, [dialog.lines, dialog.speakers.length]);

  useEffect(() => {
    if (rolePlay.isCompleted && unlocked && rolePlay.helperTextLevel === 0) {
      markMastered(dialog.slug);
    }
  }, [rolePlay.isCompleted, unlocked, rolePlay.helperTextLevel, dialog.slug, markMastered]);

  // Partner = the speaker the user is NOT playing
  const partnerSpeaker = rolePlay.selectedRole
    ? dialog.speakers.find((s) => s.name !== rolePlay.selectedRole) ?? dialog.speakers[0]!
    : dialog.speakers[0]!;
  const partnerIndex = Math.max(0, dialog.speakers.indexOf(partnerSpeaker));
  const partnerImageUrl = dialog.speaker_thumbnails?.[String(partnerIndex)]?.url ?? null;
  const gradient = LEVEL_GRADIENTS[dialog.level] ?? "from-slate-700 to-slate-900";

  // Current line
  const currentLine = speakableLines[rolePlay.currentIndex] ?? null;
  const currentSpeaker = currentLine ? dialog.speakers[currentLine.speakerIndex] ?? null : null;
  const isUserTurn = !!(currentSpeaker && currentSpeaker.name === rolePlay.selectedRole);

  // Partner's line to show at top (current line if partner's turn, or most recent partner line if user's turn)
  const partnerLineToShow = useMemo(() => {
    if (!rolePlay.isActive || !currentLine) return null;
    if (!isUserTurn) return currentLine;
    for (let i = rolePlay.currentIndex - 1; i >= 0; i--) {
      const line = speakableLines[i]!;
      const speaker = dialog.speakers[line.speakerIndex];
      if (speaker?.name !== rolePlay.selectedRole) return line;
    }
    return null;
  }, [rolePlay.isActive, rolePlay.currentIndex, isUserTurn, currentLine, speakableLines, dialog.speakers, rolePlay.selectedRole]);

  const progress = speakableLines.length > 0 ? ((rolePlay.currentIndex + 1) / speakableLines.length) * 100 : 0;

  const handleClose = useCallback(() => {
    recognitionRef.current?.abort();
    setIsListening(false);
    setTranscript("");
    setPartnerAudioPlaying(false);
    setRolePlay({ isOpen: false, isActive: false, isCompleted: false });
    const el = document.getElementById("audio-element") as HTMLAudioElement | null;
    if (el) {
      el.pause();
      el.onended = null;
    }
  }, [setRolePlay]);

  const advance = useCallback(() => {
    recognitionRef.current?.abort();
    setIsListening(false);
    setTranscript("");
    const nextIndex = rolePlay.currentIndex + 1;
    if (nextIndex >= speakableLines.length) {
      setRolePlay({ isCompleted: true });
    } else {
      setRolePlay({ currentIndex: nextIndex });
    }
  }, [rolePlay.currentIndex, speakableLines.length, setRolePlay]);

  const advanceRef = useRef(advance);
  advanceRef.current = advance;

  // Auto-play partner audio, then auto-advance when it ends
  useEffect(() => {
    if (!rolePlay.isActive || rolePlay.isCompleted || isUserTurn) return;

    const el = document.getElementById("audio-element") as HTMLAudioElement | null;
    if (!currentLine?.audioUrl || !el) {
      // No audio — auto-advance after a brief pause
      const timer = setTimeout(() => advanceRef.current(), 800);
      return () => clearTimeout(timer);
    }

    setPartnerAudioPlaying(true);
    el.src = currentLine.audioUrl;
    el.playbackRate = audio.playbackSpeed;
    const done = () => {
      setPartnerAudioPlaying(false);
      setTimeout(() => advanceRef.current(), 400);
    };
    el.onended = done;
    el.onerror = done;
    el.play().catch(done);

    return () => {
      el.onended = null;
      el.onerror = null;
    };
  }, [rolePlay.isActive, rolePlay.isCompleted, rolePlay.currentIndex, isUserTurn, audio.playbackSpeed, currentLine?.audioUrl]);

  function playLineAudio(audioUrl: string | null) {
    if (!audioUrl) return;
    const el = document.getElementById("audio-element") as HTMLAudioElement | null;
    if (!el) return;
    el.src = audioUrl;
    el.playbackRate = audio.playbackSpeed;
    el.play().catch(() => {});
  }

  const startListening = useCallback(() => {
    const SR = getSpeechRecognitionClass();
    if (!SR) return;

    recognitionRef.current?.abort();
    const recognition = new SR();
    recognition.lang = BCP47_MAP[dialog.language_code] ?? dialog.language_code.substring(0, 2).toLowerCase();
    recognition.continuous = false;
    recognition.interimResults = true;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      let result = "";
      for (let i = 0; i < event.results.length; i++) {
        result += event.results[i]![0]!.transcript;
      }
      setTranscript(result);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
    setTranscript("");
  }, [dialog.language_code]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!rolePlay.isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") handleClose();
      if (e.key === " " && rolePlay.isActive && !rolePlay.isCompleted) {
        e.preventDefault();
        advance();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [rolePlay.isOpen, rolePlay.isActive, rolePlay.isCompleted, handleClose, advance]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
    };
  }, []);

  if (!rolePlay.isOpen) return null;

  // ─── Font/direction helpers ───
  let fontClass = "";
  if (config.direction === "rtl" && !rolePlay.useTranscriptionMode) fontClass += " font-arabic";
  if (toggles.nastaliq && config.hasNastaliq && !rolePlay.useTranscriptionMode) fontClass = " font-nastaliq";
  const dir = config.direction === "rtl" && !rolePlay.useTranscriptionMode ? ("rtl" as const) : undefined;

  function getDisplayText(text: string, romanization: string | null) {
    if (rolePlay.useTranscriptionMode && romanization) return romanization;
    return text;
  }

  function getUserProcessedText() {
    if (!currentLine) return "";
    const displayText = getDisplayText(currentLine.text, currentLine.romanization);
    if (rolePlay.helperTextLevel >= 100) return escapeHtml(displayText);
    const cacheKey = `${currentLine.index}_${rolePlay.helperTextLevel}_${rolePlay.useTranscriptionMode}`;
    if (!hiddenWordsCache.current[cacheKey]) {
      hiddenWordsCache.current[cacheKey] = hideWords(displayText, rolePlay.helperTextLevel);
    }
    return hiddenWordsCache.current[cacheKey]!;
  }

  const hasSpeechRecognition = !!getSpeechRecognitionClass();

  // ─── SETUP SCREEN ───
  if (!rolePlay.isActive) {
    return (
      <div className="fixed inset-0 bg-black/95 z-[1000] flex justify-center items-center p-4">
        <div className="w-full max-w-sm bg-th-surface border border-th-border rounded-2xl p-6 sm:p-8 text-center relative shadow-2xl">
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-th-btn border border-th-border text-th-text-2 hover:bg-red-500 hover:text-white transition-colors"
            onClick={handleClose}
          >
            ✕
          </button>

          <h3 className="text-xl font-bold mb-6 mt-2">Choose Your Role</h3>

          <div className="flex flex-col gap-3 mb-6">
            {dialog.speakers.map((s, i) => {
              const thumbUrl = dialog.speaker_thumbnails?.[String(i)]?.url;
              return (
                <button
                  key={s.name}
                  className={`flex items-center gap-4 px-4 py-3 rounded-xl font-medium transition-all ${
                    rolePlay.selectedRole === s.name
                      ? "bg-glossika-600 text-white ring-2 ring-glossika-400"
                      : "bg-th-input border border-th-border text-th-text-2 hover:border-glossika-500"
                  }`}
                  onClick={() => setRolePlay({ selectedRole: s.name })}
                >
                  <div className="w-14 h-14 rounded-full overflow-hidden ring-2 ring-white/20 flex-shrink-0">
                    {thumbUrl ? (
                      <img src={thumbUrl} alt={s.name} className="w-full h-full object-cover" />
                    ) : (
                      <div
                        className={`w-full h-full flex items-center justify-center text-lg font-bold ${
                          s.gender === "male" ? "bg-blue-900/60 text-blue-300" : "bg-pink-900/60 text-pink-300"
                        }`}
                      >
                        {s.name[0]}
                      </div>
                    )}
                  </div>
                  <div className="text-left">
                    <div className="font-semibold">{s.name}</div>
                    <div className="text-xs opacity-70 capitalize">{s.gender}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-th-text-2 mb-2">Helper Text</label>
            <div className="flex items-center gap-4 justify-center">
              <input
                type="range"
                min="0"
                max="100"
                step="25"
                value={rolePlay.helperTextLevel}
                onChange={(e) => setRolePlay({ helperTextLevel: parseInt(e.target.value) })}
                className="w-40"
              />
              <span className="text-lg font-bold text-glossika-400 min-w-[50px]">
                {rolePlay.helperTextLevel}%
              </span>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex gap-2 justify-center">
              <button
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  !rolePlay.useTranscriptionMode
                    ? "bg-glossika-600 text-white"
                    : "bg-th-input border border-th-border text-th-text-2"
                }`}
                onClick={() => setRolePlay({ useTranscriptionMode: false })}
              >
                Script
              </button>
              <button
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  rolePlay.useTranscriptionMode
                    ? "bg-glossika-600 text-white"
                    : "bg-th-input border border-th-border text-th-text-2"
                }`}
                onClick={() => setRolePlay({ useTranscriptionMode: true })}
              >
                Romanization
              </button>
            </div>
          </div>

          <button
            className="w-full py-4 bg-gradient-to-r from-glossika-500 to-indigo-500 text-white text-lg font-bold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40 shadow-lg"
            onClick={() => {
              hiddenWordsCache.current = {};
              setTranscript("");
              setRolePlay({
                isActive: true,
                isCompleted: false,
                currentIndex: 0,
                cheatAttempts: 0,
                lastTapTime: Date.now(),
              });
            }}
            disabled={!rolePlay.selectedRole}
          >
            Start Role-Play
          </button>
        </div>
      </div>
    );
  }

  // ─── COMPLETION / UPGRADE SCREEN ───
  if (rolePlay.isCompleted) {
    const showUpgrade = !unlocked;

    return (
      <div className="fixed inset-0 z-[1000] bg-black">
        {partnerImageUrl ? (
          <img src={partnerImageUrl} alt="" className="absolute inset-y-0 left-1/2 -translate-x-1/2 h-full w-auto max-w-none opacity-20" />
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-b ${gradient} opacity-30`} />
        )}
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative z-10 flex flex-col items-center justify-center h-full px-6 text-center">
          {showUpgrade ? (
            <>
              <div className="text-5xl mb-4">{insufficientCredits ? "💳" : "🔒"}</div>
              <h2 className="text-2xl font-bold text-white mb-3">
                {insufficientCredits ? "No Credits Remaining" : "Preview Complete"}
              </h2>
              <p className="text-white/50 text-sm mb-8 max-w-xs">
                {!user && !emailUser
                  ? "Enter your email to continue practicing for free."
                  : !user && emailUser
                    ? "Create an account to unlock full dialogs and continue practicing."
                    : insufficientCredits
                      ? "Purchase more credits to continue unlocking dialogs."
                      : "Unlock this dialog to continue the full role-play with all lines and notes."}
              </p>
              {creditError && (
                <p className="text-red-400 text-sm mb-4">{creditError}</p>
              )}
              <div className="flex flex-col gap-3 w-full max-w-xs">
                {!user && !emailUser ? (
                  <button
                    className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-colors shadow-lg shadow-indigo-500/25"
                    onClick={() => {
                      handleClose();
                      openEmailModal();
                    }}
                  >
                    Get Started Free
                  </button>
                ) : !user && emailUser ? (
                  <button
                    className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-colors shadow-lg shadow-indigo-500/25"
                    onClick={() => {
                      handleClose();
                      openAuthModal();
                    }}
                  >
                    Create Account to Unlock
                  </button>
                ) : insufficientCredits ? (
                  <a
                    href="https://glossika.com/credits"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-4 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-xl transition-colors shadow-lg shadow-amber-500/25 block"
                  >
                    Buy Credits
                  </a>
                ) : (
                  <button
                    className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-colors shadow-lg shadow-indigo-500/25 disabled:opacity-50"
                    disabled={deducting}
                    onClick={async () => {
                      setCreditError(null);
                      const result = await deductCredit(dialog.id, dialog.title_english || dialog.slug);
                      if (result.success) {
                        onUnlock();
                        handleClose();
                      } else if (result.error === "Insufficient credits") {
                        setInsufficientCredits(true);
                      } else {
                        setCreditError(result.error ?? "Something went wrong");
                      }
                    }}
                  >
                    {deducting ? "Unlocking..." : "Use 1 Credit to Unlock"}
                  </button>
                )}
                <button
                  className="w-full py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-colors"
                  onClick={handleClose}
                >
                  Back to Dialog
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">{rolePlay.helperTextLevel === 0 ? "🏆" : "🎉"}</div>
              <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-glossika-400 to-indigo-400 bg-clip-text text-transparent">
                {rolePlay.helperTextLevel === 0 ? "Mastered!" : "Well Done!"}
              </h2>
              <p className="text-white/50 mb-8">
                {speakableLines.length} lines practiced
                {rolePlay.helperTextLevel < 100 && ` · ${rolePlay.helperTextLevel}% help`}
                {rolePlay.helperTextLevel === 0 && " · No help"}
              </p>

              <div className="flex flex-col gap-3 w-full max-w-xs">
                {rolePlay.helperTextLevel > 0 && (
                  <button
                    className="w-full py-4 bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl transition-colors"
                    onClick={() => {
                      hiddenWordsCache.current = {};
                      setTranscript("");
                      setRolePlay({
                        helperTextLevel: Math.max(0, rolePlay.helperTextLevel - 25),
                        isActive: true,
                        isCompleted: false,
                        currentIndex: 0,
                        cheatAttempts: 0,
                        lastTapTime: Date.now(),
                      });
                    }}
                  >
                    Try Harder ({Math.max(0, rolePlay.helperTextLevel - 25)}% help)
                  </button>
                )}
                <button
                  className="w-full py-4 bg-glossika-600 hover:bg-glossika-500 text-white font-bold rounded-xl transition-colors"
                  onClick={() => {
                    hiddenWordsCache.current = {};
                    setTranscript("");
                    setRolePlay({
                      isActive: true,
                      isCompleted: false,
                      currentIndex: 0,
                      cheatAttempts: 0,
                      lastTapTime: Date.now(),
                    });
                  }}
                >
                  Replay ({rolePlay.helperTextLevel}% help)
                </button>
                <button
                  className="w-full py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-colors"
                  onClick={handleClose}
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── IMMERSIVE ACTIVE PLAY ───
  if (!currentLine) return null;

  const partnerDisplayText = partnerLineToShow
    ? getDisplayText(partnerLineToShow.text, partnerLineToShow.romanization)
    : "";

  const partnerTranslation = partnerLineToShow
    ? partnerLineToShow.translation.en || partnerLineToShow.translation.english || Object.values(partnerLineToShow.translation)[0]
    : "";

  return (
    <div className="fixed inset-0 z-[1000] bg-black select-none">
      {/* Partner image: full height, centered, sides crop on wide screens */}
      {partnerImageUrl ? (
        <img
          src={partnerImageUrl}
          alt={partnerSpeaker.name}
          className="absolute inset-y-0 left-1/2 -translate-x-1/2 h-full w-auto max-w-none object-contain"
        />
      ) : (
        <div className={`absolute inset-0 bg-gradient-to-b ${gradient}`} />
      )}
      <div className="absolute inset-0 bg-black/30" />

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 z-20 h-1 bg-white/10">
        <div
          className="h-full bg-glossika-500 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Close button */}
      <button
        className="absolute top-3 right-3 z-30 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm text-white/70 hover:text-white hover:bg-black/70 transition-colors flex items-center justify-center text-lg"
        onClick={handleClose}
      >
        ✕
      </button>

      {/* Line counter */}
      <div className="absolute top-3 left-3 z-20 text-white/50 text-xs bg-black/40 backdrop-blur-sm rounded-full px-3 py-1.5 font-medium">
        {rolePlay.currentIndex + 1} / {speakableLines.length}
      </div>

      {/* ─── TOP: Partner's line ─── */}
      <div className="absolute top-0 left-0 right-0 z-10 pt-14 pb-10 px-5 bg-gradient-to-b from-black/80 via-black/50 to-transparent">
        {partnerLineToShow && (
          <div className="max-w-lg mx-auto">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-white/50 text-xs font-semibold uppercase tracking-wider">
                {partnerSpeaker.name}
              </span>
              {!isUserTurn && partnerAudioPlaying && (
                <div className="flex items-center gap-[2px]">
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      className="w-[2px] bg-glossika-400 rounded-sm"
                      style={{ height: "10px", animation: `wave 0.8s ease-in-out infinite ${i * 0.1}s` }}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className={`text-white text-xl sm:text-2xl leading-relaxed font-medium${fontClass}`} dir={dir}>
              {partnerDisplayText}
            </div>
            {partnerLineToShow.romanization && !rolePlay.useTranscriptionMode && (
              <div className="text-white/40 text-sm italic mt-1">{partnerLineToShow.romanization}</div>
            )}
            {partnerTranslation && (
              <div className="text-white/30 text-sm mt-1">{partnerTranslation}</div>
            )}
          </div>
        )}
      </div>

      {/* ─── BOTTOM: User turn or Partner continue ─── */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div
          className="bg-gradient-to-t from-black/95 via-black/80 to-transparent pt-20 px-5"
          style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom, 2rem))" }}
        >
          <div className="max-w-lg mx-auto">
            {isUserTurn ? (
              <>
                {/* User's target text */}
                <div className="text-green-400/80 text-xs font-bold uppercase tracking-wider mb-2">
                  Your turn
                </div>
                <div
                  className={`text-white text-2xl sm:text-3xl leading-relaxed font-medium mb-1${fontClass}`}
                  dir={dir}
                  dangerouslySetInnerHTML={{ __html: getUserProcessedText() }}
                />
                {currentLine.romanization && !rolePlay.useTranscriptionMode && (
                  <div className="text-white/30 text-sm italic mb-3">{currentLine.romanization}</div>
                )}

                {/* Hear how it sounds */}
                {currentLine.audioUrl && (
                  <button
                    className="flex items-center gap-1.5 text-white/40 hover:text-white/70 text-xs mb-4 transition-colors"
                    onClick={() => playLineAudio(currentLine.audioUrl)}
                  >
                    <PlayIcon />
                    <span>Hear it</span>
                  </button>
                )}

                {/* Speech recognition area */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-4">
                  {transcript ? (
                    <p className="text-white/80 text-lg text-center min-h-[28px]">{transcript}</p>
                  ) : (
                    <p className="text-white/25 text-center min-h-[28px]">
                      {isListening ? "Listening..." : hasSpeechRecognition ? "Tap mic to speak" : "Practice, then continue"}
                    </p>
                  )}

                  {hasSpeechRecognition && (
                    <div className="flex justify-center mt-3">
                      <button
                        className={`w-14 h-14 rounded-full flex items-center justify-center text-white transition-all ${
                          isListening
                            ? "bg-red-500 shadow-lg shadow-red-500/40 animate-pulse"
                            : "bg-white/20 hover:bg-white/30"
                        }`}
                        onClick={isListening ? stopListening : startListening}
                      >
                        <MicIcon />
                      </button>
                    </div>
                  )}
                </div>

                <button
                  className="w-full py-4 bg-glossika-600 hover:bg-glossika-500 active:bg-glossika-700 text-white font-bold text-lg rounded-xl transition-colors"
                  onClick={advance}
                >
                  Continue
                </button>
              </>
            ) : (
              <div className="flex items-center justify-center gap-2 py-4 text-white/40">
                {partnerAudioPlaying && (
                  <>
                    <div className="flex items-center gap-[2px]">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <span
                          key={i}
                          className="w-[2px] bg-glossika-400 rounded-sm"
                          style={{ height: "14px", animation: `wave 0.8s ease-in-out infinite ${i * 0.1}s` }}
                        />
                      ))}
                    </div>
                    <span className="text-sm">Listening...</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
